import { Component, computed, input, OnInit, output, signal } from "@angular/core";
import { ControlContainer, NgForm } from "@angular/forms";
import { lastValueFrom } from "rxjs";
import {
  FormsIds,
  ServiceRequestStepType,
} from "src/app/components/service-request/models/ServiceRequestStepType";
import LocalizationConstants from "src/app/constants/localization-constants";
import { Address } from "src/app/models/Address";
import CustomerSettings from "src/app/models/CustomerSettings";
import { UserVendor } from "src/app/models/UserVendor";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { RaBasketService } from "src/app/services/ra-basket.service";

@Component({
  selector: "app-service-request-step-one",
  templateUrl: "./service-request-step-one.component.html",
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  standalone: false,
})
export class ServiceRequestStepOneComponent implements OnInit {
  isShipmentRequested = input.required<boolean>();
  isShipmentRequestedChange = output<boolean>();
  shipFromList = signal<Address[]>([]);
  vendorName = input<string>();
  vendorSelected = computed(
    () => this.availableVendorList().find((v) => v.vendorName === this.vendorName()) || null,
  );
  vendorNameChange = output<string>();

  isRaResultReceived = input<boolean>();
  showValidationErrors = input<boolean>(false);
  customerSettings = input<CustomerSettings>();
  vendorList = input<UserVendor[]>([]);
  currentShipFrom = input<Address>();
  currentShipFromChange = output<Address>();

  shipFromOptionSelected = computed<Address | null>(
    () =>
      (this.isOtherShipFromOptionSelected() ? this.otherShipFromOption : this.currentShipFrom()) ??
      null,
  );

  isOtherShipFromOptionSelected = computed(
    () => this.currentShipFrom()?.name === this.otherShipFromOptionName,
  );

  otherAddressInput = signal("");

  isOtherAddressInvalid = computed<boolean>(() => {
    return (
      this.showValidationErrors() &&
      this.isOtherShipFromOptionSelected() &&
      !this.otherAddressInput()?.trim()
    );
  });

  serviceRequestStepType = ServiceRequestStepType;
  formsIds = FormsIds;

  availableShipFromList = computed(
    () =>
      this.shipFromList()?.filter(
        (address) => !this.isShipmentRequested() || address.isMontovaCompatible,
      ) ?? [],
  );

  availableVendorList = computed(
    () =>
      this.vendorList()?.filter(
        (address) => !this.isShipmentRequested() || address.isMontovaCompatible,
      ) ?? [],
  );

  localizationConstants = LocalizationConstants;

  readonly otherShipFromOptionName = "other";
  readonly otherShipFromOption = { ...new Address(), name: this.otherShipFromOptionName };

  constructor(
    public companyService: CompanyService,
    private basket: RaBasketService,
    private globalOverlayService: GlobalOverlayService,
  ) {
    this.isShipmentRequestedChange.subscribe((isShipmentRequested: boolean) => {
      if (!isShipmentRequested) return;

      if (!this.currentShipFrom()?.isMontovaCompatible) {
        this.currentShipFromChange.emit(null);
      }

      if (!this.vendorSelected()?.isMontovaCompatible) this.vendorNameChange.emit(null);
    });
  }

  async ngOnInit() {
    this.globalOverlayService.show();
    await this.getShipFromAsync();
    this.globalOverlayService.close();
  }

  async getShipFromAsync(): Promise<void> {
    const shipFromList = await lastValueFrom(
      this.companyService.getShipFromList(this.companyService.selectedCompany.name),
    );
    this.shipFromList.update(() => {
      return shipFromList;
    });

    const shipFrom = this.basket.tools?.find((x) => x.shipTo != null)?.shipTo;
    this.currentShipFromChange.emit(this.shipFromList().find((x) => x.name === shipFrom));
  }

  onShipFromOptionSelected(shipFromOption: Address) {
    this.otherAddressInput.update(() => "");
    this.currentShipFromChange.emit(shipFromOption);
  }

  onOtherShipFromAddressInput(addressInputEvent: KeyboardEvent) {
    const addressInput = (addressInputEvent.target as HTMLInputElement).value;
    this.otherAddressInput.update(() => addressInput);
    this.currentShipFromChange.emit({
      ...new Address(),
      displayName: addressInput,
      name: this.otherShipFromOptionName,
    });
  }
}
